import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import Button from '@mui/material/Button';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import Alert from '../Alert';
import ModalBaseWithHeader from './ModalBaseWithHeader';
import LoadingIndicator from '../LoadingIndicator';
import { useBulkAddPlayersToTeamMutation } from '../../api/leaguesApi';
import { parsePlayerIds } from '../../utils/playerSearch';
import { getAddPlayerWarningMessage } from '../../utils/addPlayerErrors';
import { showErrorPopup } from '../../redux/errorPopupSlice';
import { bulkAddPlayersModalPropTypes } from '../../utils/proptypes';

const BulkAddPlayersModal = ({ open, onClose, sessionId, teams, defaultTeamId }) => {
    const dispatch = useDispatch();
    const [teamId, setTeamId] = useState(defaultTeamId || '');
    const [playerIdText, setPlayerIdText] = useState('');
    const [failedRegistrants, setFailedRegistrants] = useState([]);
    const [successCount, setSuccessCount] = useState(null);
    const [warningMessage, setWarningMessage] = useState('');
    const [bulkAddPlayersToTeam, { isLoading }] = useBulkAddPlayersToTeamMutation();

    useEffect(() => {
        if (open) {
            setTeamId(defaultTeamId || '');
            setPlayerIdText('');
            setFailedRegistrants([]);
            setSuccessCount(null);
            setWarningMessage('');
        }
    }, [open, defaultTeamId]);

    const playerIds = parsePlayerIds(playerIdText);
    const canRegister = !!teamId && playerIds.length > 0 && !isLoading;

    const handleRegister = async () => {
        setWarningMessage('');

        try {
            const response = await bulkAddPlayersToTeam({ teamId, playerIds, sessionId }).unwrap();
            const failed = response?.failedRegistrants || [];
            setFailedRegistrants(failed);
            setSuccessCount(response?.successfulRegistrants?.length || 0);
            // Keep only the failed IDs in the box so the organizer can correct and retry.
            const failedIds = new Set(failed.map((registrant) => registrant.playerId));
            setPlayerIdText(playerIds.filter((id) => failedIds.has(id)).join('\n'));
        } catch (error) {
            const warning = getAddPlayerWarningMessage(error, 'Unable to add these players to the team.');

            if (warning) {
                setWarningMessage(warning);
            } else {
                dispatch(showErrorPopup(error));
            }
        }
    };

    return (
        <ModalBaseWithHeader title="Add Players by ID" onClose={onClose} open={open}>
            <DialogContent sx={{ p: 4 }} dividers>
                <FormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel id="bulk-add-team">Team</InputLabel>
                    <Select
                        value={teamId}
                        label="Team"
                        labelId="bulk-add-team"
                        onChange={(event) => setTeamId(event.target.value)}
                    >
                        {teams?.map((team) => (
                            <MenuItem key={team.id} value={team.id}>
                                {team.description}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <TextField
                    label="Player IDs (one per line)"
                    multiline
                    minRows={6}
                    fullWidth
                    value={playerIdText}
                    onChange={(event) => setPlayerIdText(event.target.value)}
                    placeholder={'3068457\n3074042\n3074073'}
                />
                {warningMessage && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        {warningMessage}
                    </Alert>
                )}
                {successCount !== null && (
                    <Alert severity={failedRegistrants.length ? 'warning' : 'success'} sx={{ mt: 2 }}>
                        {`${successCount} player${successCount === 1 ? '' : 's'} added${
                            failedRegistrants.length
                                ? `, ${failedRegistrants.length} could not be added`
                                : ''
                        }.`}
                    </Alert>
                )}
                {failedRegistrants.length > 0 && (
                    <Table size="small" sx={{ mt: 2 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Player Id</TableCell>
                                <TableCell>Failure Reason</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {failedRegistrants.map((registrant) => (
                                <TableRow key={registrant.playerId}>
                                    <TableCell>{registrant.playerId}</TableCell>
                                    <TableCell>{registrant.failureReason}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
                {isLoading && <LoadingIndicator />}
            </DialogContent>
            <DialogActions sx={{ display: 'flex', justifyContent: 'center' }}>
                <Button variant="dark" autoFocus onClick={handleRegister} disabled={!canRegister}>
                    Register
                </Button>
            </DialogActions>
        </ModalBaseWithHeader>
    );
};

BulkAddPlayersModal.propTypes = {
    ...bulkAddPlayersModalPropTypes,
};

BulkAddPlayersModal.defaultProps = {
    open: false,
};

export default BulkAddPlayersModal;
